import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import GridOnIcon from "@mui/icons-material/GridOn";
import { CircularProgress, IconButton, ListItemIcon, ListItemText, MenuItem, MenuList, Tooltip } from "@mui/material";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemReactTinyPopover } from "@/ui/Popups/SystemReactTinyPopover";
import type { ExportFormat } from "./exportGridData";

const FORMAT_MENU: Record<ExportFormat, { labelKey: string; Icon: typeof GridOnIcon }> = {
	xlsx: { labelKey: "system.dataGrid.exportXlsx", Icon: GridOnIcon },
	csv: { labelKey: "system.dataGrid.exportCsv", Icon: DescriptionIcon },
};

/** The `fontSize="small"` glyph's box, so the spinner that replaces it doesn't resize the button. */
const EXPORTING_SPINNER_SIZE_PX = 20;

interface GridExportButtonProps {
	onExport: (format: ExportFormat) => void;
	/** A file is being built: the button shows a spinner and takes no clicks, so a double click downloads once. */
	isExporting: boolean;
	/** Formats to offer, in menu order. With exactly one, the button downloads it directly (no menu). */
	formats: readonly ExportFormat[];
	disabled?: boolean;
	dataTestId?: string;
}

/** Toolbar download button: opens a small menu to pick the export format, or downloads the only one offered. */
export const GridExportButton = memo(function GridExportButton({ onExport, isExporting, formats, disabled, dataTestId }: GridExportButtonProps) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const onlyFormat = formats.length === 1 ? formats[0] : undefined;

	const handlePick = useCallback(
		(format: ExportFormat) => {
			setIsOpen(false);
			onExport(format);
		},
		[onExport],
	);

	return (
		<SystemReactTinyPopover
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			positions={["bottom", "top", "left", "right"]}
			align="end"
			content={
				<MenuList disablePadding>
					{formats.map((format) => {
						const { labelKey, Icon } = FORMAT_MENU[format];
						return (
							<MenuItem key={format} onClick={() => handlePick(format)} data-testid={dataTestId ? `${dataTestId}-${format}` : undefined}>
								<ListItemIcon>
									<Icon fontSize="small" />
								</ListItemIcon>
								<ListItemText primary={t(labelKey)} />
							</MenuItem>
						);
					})}
				</MenuList>
			}
		>
			<Tooltip title={t("system.dataGrid.export")}>
				{/* span so the tooltip still works while the button is disabled */}
				<span>
					<IconButton
						size="small"
						color="primary"
						aria-label={t("system.dataGrid.export")}
						onClick={() => (onlyFormat ? onExport(onlyFormat) : setIsOpen((prev) => !prev))}
						disabled={disabled || isExporting}
						aria-busy={isExporting}
						data-testid={dataTestId}
					>
						{isExporting ? (
							<CircularProgress size={EXPORTING_SPINNER_SIZE_PX} aria-hidden data-testid={dataTestId ? `${dataTestId}-loading` : undefined} />
						) : (
							<FileDownloadIcon fontSize="small" />
						)}
					</IconButton>
				</span>
			</Tooltip>
		</SystemReactTinyPopover>
	);
});
